import { useCallback, useEffect, useMemo, useRef } from "react";

import { useChat } from "../state/useChat";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { buildAutoMapPrompt } from "../components/autoMapPrompt";
import {
  decideMapping,
  hasOutputFrame,
  isWorkingFrame,
  workingDataSignature,
} from "../components/mappingFreshness";
import { STAGE_LIVE } from "../components/stages";

/**
 * Runs the AI mapping when the user opens Map & review — the first time, and again whenever the working
 * data has moved since the last run.
 *
 * Before this, that stage did nothing on its own: there is no mapping endpoint and no button anywhere, so
 * the only way an output frame ever appeared was the user typing a request in chat, which the stage's own
 * empty state had to instruct them to do. Now the stage is locked read-only, so it could not even do that.
 *
 * ## The two pieces of state, and why they are refs
 *
 * `mappedRef` is the working-data signature the current output frame was built from — the answer to "has
 * anything changed since?". `recordArmedRef` says whether changes arriving right now should be folded into
 * it. Both are refs rather than reducer fields, and that is not a shortcut:
 *
 *  - Nothing renders them, so state would buy nothing.
 *  - A ref is written synchronously. StrictMode invokes an effect twice with the same closure, and reducer
 *    state dispatched by the first invocation is not visible to the second — so a state-based guard sends
 *    two mapping turns, the second of which waits on the session write lock the first holds and surfaces
 *    the "waiting for the lock" notice. The same shape of bug is documented for the greeting in
 *    SessionLayout. A ref set before the send closes it.
 *
 * ## Why the arming exists
 *
 * A mapping turn writes to working frames too — fuzzy-match corrections, unit normalisation — so its own
 * side effects would otherwise leave the signature ahead of `mappedRef` the moment it finished, and every
 * later visit to Map & review would map again, drift again, for ever. While armed, each list refresh moves
 * `mappedRef` forward, so a turn's own writes cannot make its output look stale.
 *
 * Armed when we launch, and when ANY successful `create_dataframe` produces an output frame
 * (`outputCreatedCounter`) — which covers the mapping the user asked for by hand from Preview & transform.
 * Without that second trigger the panel's jump to Map & review would be read as a first arrival, and the
 * commonest happy path would run a second, redundant mapping turn straight after the first.
 *
 * Disarmed by `noteUserSend`, on a message the user sent from anywhere but this stage. That is precisely
 * the event meaning "the working data is about to change for real", and it is available because every
 * change starts either with an app-initiated mapping or with a user message — there is no third writer.
 */
export function useAutoMap({ sendAutoMessage }) {
  const { sessionId, aiIngestTarget, hasTaskTemplate, sessionKind } = useSession();
  const { isConnected, isTurnActive } = useChat();
  const {
    stage,
    dataframes,
    isLoadingList,
    outputCreatedCounter,
    selectedOutputKey,
  } = useWorkingPanel();

  const isFoundation = sessionKind === "foundation_v2";
  // The frame list is a plain array replaced only by SET_DATAFRAMES, so memoising on it is enough — and it
  // matters, because the signature goes in an effect's dependencies and a fresh array every render would
  // re-run the effect every render.
  const signature = useMemo(() => workingDataSignature(dataframes), [dataframes]);
  const hasOutput = useMemo(() => hasOutputFrame(dataframes), [dataframes]);
  // `dataframes` starts empty and `isLoadingList` starts FALSE, so there is a window on mount where both
  // read "ready" with nothing in them. An empty list is what "not loaded" looks like here.
  const isListReady = !isLoadingList && dataframes.length > 0;

  const mappedRef = useRef(null);
  const recordArmedRef = useRef(false);

  // Nothing switches sessions in place today (the picker only exists during bootstrap, and the provider
  // sits above SessionLayout), but the refs would outlive one if anything ever did — and a signature from
  // another session's frames means "already mapped" about an output that does not exist.
  useEffect(() => {
    mappedRef.current = null;
    recordArmedRef.current = false;
  }, [sessionId]);

  // Declared BEFORE the decision effect below so that on the commit where an output frame appears, the
  // arming is in place before anything decides whether to map.
  useEffect(() => {
    if (outputCreatedCounter === 0) return;
    recordArmedRef.current = true;
  }, [outputCreatedCounter]);

  const launch = useCallback(
    (force) => {
      const frames = dataframes.filter(isWorkingFrame).map((d) => d.key);
      const { text, display } = buildAutoMapPrompt({
        frames,
        target: aiIngestTarget || "",
        // The frame the user is reviewing, so a re-map rebuilds THAT one instead of adding a second
        // output beside it. `selectedOutputKey` is reconciled in WorkingPanel and is what the Commit
        // stage commits, so it is the right frame by construction.
        outputFrame: selectedOutputKey || "",
        isRemap: Boolean(force),
      });
      // Both set before the send, never after: `sendAutoMessage` returns false when it queues on a socket
      // that has not opened yet and still sends later, so there is no success to wait for — and a second
      // effect invocation reading an unset ref is the double-turn bug.
      mappedRef.current = signature;
      recordArmedRef.current = true;
      sendAutoMessage(text, display);
    },
    [dataframes, aiIngestTarget, selectedOutputKey, signature, sendAutoMessage]
  );

  // One effect, re-evaluated whenever anything it reads changes. That is deliberately the whole retry
  // mechanism: an arrival that lands before the frame list has loaded, or while the agent is mid-turn,
  // decides "wait" and is simply decided again when that stops being true. A remembered "pending" flag
  // would have to be cleared correctly on every path that makes it moot.
  useEffect(() => {
    if (isListReady && recordArmedRef.current) {
      // A change that arrived from the mapping turn itself. Recorded before the decision, or the turn's
      // own writes make its output look stale the moment it finishes.
      mappedRef.current = signature;
    }

    const decision = decideMapping({
      stage,
      signature,
      mappedSignature: mappedRef.current,
      hasOutput,
      isListReady,
      isFoundation,
      // Either is enough: a deep link carries the target, and a session that agreed one in chat has read
      // the template. Neither means the turn could only end in a question nobody can answer from here.
      hasDestination: Boolean(aiIngestTarget) || Boolean(hasTaskTemplate),
      isConnected,
      isTurnActive,
    });

    if (decision === "seed") {
      // Reopening a session that already has an output: take it as matching the working data rather than
      // spending a turn rebuilding something the user may already have reviewed.
      mappedRef.current = signature;
      return;
    }
    if (decision === "map") launch(false);
  }, [
    stage,
    signature,
    hasOutput,
    isListReady,
    isFoundation,
    aiIngestTarget,
    hasTaskTemplate,
    isConnected,
    isTurnActive,
    launch,
  ]);

  return {
    /** "Re-map with AI" — runs regardless of whether anything changed. */
    remap: useCallback(() => launch(true), [launch]),
    /**
     * Called for every message the user sends. A send from anywhere but Map & review is the start of a
     * change the user meant to make, so whatever it does to the working data must be allowed to show up
     * as "the output is stale". A send from Map & review can only be an answer to the mapping turn (the
     * composer is closed there; only the agent's own widgets can send), so the arming stands.
     */
    noteUserSend: useCallback(() => {
      if (stage !== STAGE_LIVE) recordArmedRef.current = false;
    }, [stage]),
  };
}
